import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, serverTimestamp, updateDoc, where } from 'firebase/firestore'
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { auth, db, storage } from '~~/firebaseConfig'

// Structured Outputs 스키마는 functions/ai/analyzeMarketingProfile.js에서 관리한다.
interface CampaignAnalysisResult {
  keywords: string[]
  analyzedAt?: string // 분석 시점 추가
}

export const useCampaignStore = defineStore('campaign', () => {
  const systemStore = useSystemStore()
  // const { t } = useI18n()
  const loading = ref(false)
  const documents = ref<any[]>([])
  const document = ref<any>()
  const uploadDocument = ref<any>()
  const currentView = ref<'list' | 'add' | 'edit' | 'detail'>('list')
  const userStore = useUserStore()
  const selectedBrandImageName = ref()
  const isUploadingImage = ref()
  const isEditMode = ref(false)
  const selectedCampaignImageName = ref()
  const isUploadingCampaignImage = ref(false)

  async function getCampaignList() {
    loading.value = true
    documents.value = []
    try {
      const docSnap = await getDocs(collection(db, 'campaign'))

      if (docSnap.empty) {
        documents.value = []
      }
      else {
        docSnap.forEach((doc) => {
          const data = doc.data()
          documents.value.push({
            firestoreId: doc.id, // 실제 Firestore 문서 ID
            ...data, // 문서 내부 데이터 (내부 id 필드 포함)
            id: doc.id, // Firestore 문서 ID로 덮어쓰기
          })
        })
      }
    }
    catch (error) {
      console.error(error)
    }
    finally {
      loading.value = false
    }
  }

  async function getCampaign(id: string) {
    loading.value = true
    document.value = null
    try {
      const docRef = doc(db, 'campaign', id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        // ⚠️ 스프레드 순서 주의: ...docSnap.data()를 뒤에 두면 문서 내부에 남아있는
        // 레거시 id 필드(예: "campaign_...")가 실제 Firestore 문서 ID(docSnap.id)를
        // 덮어써버린다. 2026-09-10 admin/campaign/[id].vue 승인 버튼이 존재하지
        // 않는 문서를 업데이트하려다 "No document to update" 에러로 실패하는 버그로
        // 발견됨 - id는 반드시 마지막에 덮어써서 실제 문서 ID가 이기도록 한다.
        document.value = { ...docSnap.data(), id: docSnap.id }
        return
      }

      const allCampaignsSnapshot = await getDocs(collection(db, 'campaign'))

      for (const doc of allCampaignsSnapshot.docs) {
        const data = doc.data()
        if (data.id === id) {
          // 위와 동일한 이유로 id를 마지막에 덮어쓴다.
          document.value = { ...data, id: doc.id }
          return
        }
      }
      document.value = null
    }
    catch (error) {
      console.error(error)
    }
    finally {
      loading.value = false
    }
  }

  async function addCampaign() {
    loading.value = true
    try {
      if (!uploadDocument.value) {
        systemStore.showError('등록할 캠페인 데이터가 없습니다.')
        return
      }

      // 캠페인 데이터 준비 (AI 분석 없이 먼저 저장)
      const campaignData = {
        ...uploadDocument.value,
        createdAt: serverTimestamp(),
        keywords: [], // 빈 배열로 초기화, 나중에 백그라운드에서 채움
        analyzedAt: null, // 분석 완료 시점은 나중에 업데이트
      }

      // 캠페인 데이터를 먼저 저장 (사용자는 즉시 다음 단계로) - 백그라운드에서 처리
      addDoc(collection(db, 'campaign'), campaignData).then((campaignDocRef) => {
        // 백그라운드에서 AI 분석 수행
        saveAnalysisResultBackground(
          campaignDocRef.id,
          uploadDocument.value,
        )
      }).catch((error) => {
        console.error('캠페인 저장 중 오류:', error)
        systemStore.showError('캠페인 저장에 실패했습니다.')
      })

      // 즉시 성공 메시지 표시 (Firestore 저장과 AI 분석 완료를 기다리지 않음)
      systemStore.showSuccess('캠페인이 등록되었습니다. AI 분석은 백그라운드에서 진행됩니다.')
    }
    catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      systemStore.showError(`캠페인 등록 중 오류가 발생했습니다: ${errorMessage}`)
    }
    finally {
      loading.value = false
    }
  }

  async function updateCampaign(id: string) {
    loading.value = true
    try {
      if (!uploadDocument.value) {
        systemStore.showError('수정할 캠페인 데이터가 없습니다.')
        return
      }

      // 캠페인 데이터 먼저 수정 (AI 분석은 백그라운드에서)
      const updateData = {
        ...uploadDocument.value,
        updatedAt: serverTimestamp(),
      }

      const docRef = doc(db, 'campaign', id)
      updateDoc(docRef, updateData).then(() => {
        // 백그라운드에서 AI 분석 수행
        saveAnalysisResultBackground(
          id,
          uploadDocument.value,
        )
      }).catch((error) => {
        console.error('캠페인 수정 중 오류:', error)
        systemStore.showError('캠페인 수정에 실패했습니다.')
      })
    }
    catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      console.error('캠페인 수정 중 오류:', errorMessage)
    }
    finally {
      loading.value = false
    }
  }

  async function deleteCampaign(id: string) {
    loading.value = true
    try {
      const docRef = doc(db, 'campaign', id)
      await deleteDoc(docRef)
      // systemStore.showSuccess('캠페인 삭제가 완료되었습니다.')
    }
    catch (error) {
      console.error(error)
      // systemStore.showError(t('common.error_occurred'))
    }
    finally {
      loading.value = false
    }
  }

  // 캠페인 데이터 분석 및 키워드 도출 (OpenAI Structured Outputs 적용)
  async function analyzeCampaignData(campaignData?: any, forceReanalysis = false): Promise<CampaignAnalysisResult | null> {
    try {
      // 분석할 캠페인 데이터 준비
      const dataToAnalyze = campaignData || uploadDocument.value

      if (!dataToAnalyze) {
        return null
      }

      // 🔍 중복 분석 방지: 이미 분석된 캠페인인지 확인
      if (!forceReanalysis && dataToAnalyze.firestoreId) {
        try {
          const docRef = doc(db, 'campaign', dataToAnalyze.firestoreId)
          const docSnap = await getDoc(docRef)

          if (docSnap.exists()) {
            const existingData = docSnap.data()
            const existingKeywords = existingData.keywords as CampaignAnalysisResult[]

            if (existingKeywords && existingKeywords.length > 0) {
              const latestResult = existingKeywords[existingKeywords.length - 1]
              return latestResult || null // 가장 최근 분석 결과 반환
            }
          }
        }
        catch (checkError) {
          console.error('캠페인 중복 분석 확인 중 오류:', checkError)
        }
      }
      const campaignText = `
        캠페인명: ${dataToAnalyze.step2?.campaignName || ''}
        브랜드명: ${dataToAnalyze.step1?.brandName || ''}
        제품/서비스명: ${dataToAnalyze.step1?.productServiceName || ''}
        캠페인 설명: ${dataToAnalyze.step2?.campaignDescription || ''}
        카테고리: ${dataToAnalyze.step2?.categories?.join(', ') || ''}
        키워드: ${dataToAnalyze.step2?.keywords || ''}
        작업 유형: ${dataToAnalyze.step2?.workType || ''}
        제공 내역: ${dataToAnalyze.step2?.providedItems || ''}
        캠페인 미션: ${dataToAnalyze.step2?.campaignMission || ''}
        기타 사항: ${dataToAnalyze.step2?.additionalNotes || ''}
      `.trim()
      return await requestAIAnalysis('campaign', campaignText)
    }
    catch (error) {
      console.error('캠페인 AI 분석 실패:', error)
      systemStore.showError(getAIAnalysisErrorMessage(error))
      return null
    }
  }

  // 기존 캠페인에 AI 분석 결과(키워드) 추가
  async function _addKeywordToExistingCampaign(campaignId: string) {
    try {
      // 캠페인 데이터 가져오기
      await getCampaign(campaignId)

      if (!document.value) {
        return null
      }

      // AI 분석 실행
      const analysisResult = await analyzeCampaignData(document.value)

      if (analysisResult) {
        // 캠페인 문서에 keywords 필드 업데이트
        const docRef = doc(db, 'campaign', campaignId)
        await updateDoc(docRef, {
          keywords: analysisResult,
          analyzedAt: new Date().toISOString(),
          updatedAt: serverTimestamp(),
        })

        return analysisResult
      }

      return null
    }
    catch (error) {
      console.error('키워드 추가 중 오류:', error)
      return null
    }
  }

  // campaigns 컬렉션에서 완료된 캠페인만 가져오기
  async function getCompleteCampaigns() {
    loading.value = true
    documents.value = []
    try {
      // 먼저 모든 캠페인을 가져와서 상태 확인
      const allCampaignsSnapshot = await getDocs(collection(db, 'campaign'))

      const allStatuses: string[] = []
      allCampaignsSnapshot.forEach((doc) => {
        const data = doc.data() as any
        allStatuses.push(data.status || 'undefined')
      })

      // campaign 컬렉션에서 완료된 캠페인만 쿼리
      const q = query(
        collection(db, 'campaign'),
        where('status', 'in', ['complete', 'approved']),
      )

      const campaignSnapshot = await getDocs(q)

      const completeCampaigns: any[] = []
      campaignSnapshot.forEach((doc) => {
        const campaignData = {
          firestoreId: doc.id, // 실제 Firebase 문서 ID
          ...doc.data(), // 문서 내부 데이터 (내부 id 필드 포함)
        } as any
        completeCampaigns.push(campaignData)
      })

      documents.value = completeCampaigns
    }
    catch (error) {
      console.error('완료된 캠페인 가져오기 중 오류:', error)
      documents.value = []
    }
    finally {
      loading.value = false
    }
  }

  // Admin용 모든 캠페인 가져오기 (상태 무관)
  async function getAllCampaigns() {
    loading.value = true
    documents.value = []
    try {
      // campaign 컬렉션의 모든 캠페인 가져오기
      const campaignSnapshot = await getDocs(collection(db, 'campaign'))

      const allCampaigns: any[] = []
      campaignSnapshot.forEach((doc) => {
        // id는 마지막에 덮어쓴다(getCampaign()과 동일한 이유 - 위 주석 참고).
        const campaignData = { ...doc.data(), id: doc.id }
        allCampaigns.push(campaignData)
      })

      documents.value = allCampaigns
    }
    catch (error) {
      console.error('모든 캠페인 가져오기 중 오류:', error)
      documents.value = []
    }
    finally {
      loading.value = false
    }
  }

  // 특정 광고주의 캠페인 가져오기
  async function getCampaignsByAdvertiser(advertiserId: string) {
    loading.value = true
    documents.value = []
    try {
      // campaign 컬렉션에서 특정 광고주의 캠페인만 쿼리
      const q = query(
        collection(db, 'campaign'),
        where('advertiserId', '==', advertiserId),
      )
      const campaignSnapshot = await getDocs(q)

      campaignSnapshot.forEach((doc) => {
        documents.value.push({ campaignId: doc.id, ...doc.data() })
      })
    }
    catch (error) {
      console.error('특정 광고주 캠페인 가져오기 중 오류:', error)
      documents.value = []
    }
    finally {
      loading.value = false
    }
  }

  // 현재 진행 중인 캠페인 가져오기 (날짜 기준)
  async function getActiveCampaigns() {
    loading.value = true
    documents.value = []
    try {
      // 모든 캠페인을 가져와서 날짜 필터링
      const campaignSnapshot = await getDocs(collection(db, 'campaign'))

      const currentDate = new Date()
      currentDate.setHours(0, 0, 0, 0) // 오늘 00:00:00으로 설정

      const activeCampaigns: any[] = []

      campaignSnapshot.forEach((doc) => {
        const campaignData = {
          firestoreId: doc.id, // 실제 Firestore 문서 ID
          ...doc.data(), // 문서 내부 데이터
          id: doc.id, // Firestore 문서 ID로 덮어쓰기
        } as any

        const step2 = campaignData.step2
        if (!step2 || !step2.period || !step2.periodclose) {
          return
        }

        // 날짜 파싱 (Firestore Timestamp 또는 문자열 형태)
        let startDate: Date
        let endDate: Date

        try {
          // Firestore Timestamp 객체인 경우
          if (step2.period.toDate) {
            startDate = step2.period.toDate()
          }
          else {
            startDate = new Date(step2.period)
          }

          if (step2.periodclose.toDate) {
            endDate = step2.periodclose.toDate()
          }
          else {
            endDate = new Date(step2.periodclose)
          }

          // 날짜 유효성 검사
          if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
            return
          }

          // 시간을 00:00:00으로 설정하여 날짜만 비교
          startDate.setHours(0, 0, 0, 0)
          endDate.setHours(23, 59, 59, 999) // 종료일은 23:59:59까지 포함

          // 현재 날짜가 캠페인 기간 내에 있는지 확인
          const isActive = currentDate >= startDate && currentDate <= endDate

          if (isActive) {
            activeCampaigns.push(campaignData)
          }
        }
        catch (dateError) {
          console.error('캠페인 날짜 파싱 중 오류:', dateError)
        }
      })

      // 시작일 기준으로 정렬 (최신 시작일부터)
      activeCampaigns.sort((a, b) => {
        const aStartDate = a.step2?.period?.toDate ? a.step2.period.toDate() : new Date(a.step2?.period)
        const bStartDate = b.step2?.period?.toDate ? b.step2.period.toDate() : new Date(b.step2?.period)
        return bStartDate.getTime() - aStartDate.getTime()
      })

      documents.value = activeCampaigns
    }
    catch (error) {
      console.error('현재 진행 중인 캠페인 가져오기 중 오류:', error)
      documents.value = []
    }
    finally {
      loading.value = false
    }
  }

  // 진행 예정 캠페인 가져오기 (시작일이 현재 날짜 이후인 캠페인들)
  async function getUpcomingCampaigns() {
    loading.value = true
    documents.value = []
    try {
      // 모든 캠페인을 가져와서 날짜 필터링
      const campaignSnapshot = await getDocs(collection(db, 'campaign'))
      const currentDate = new Date()
      currentDate.setHours(23, 59, 59, 999) // 오늘 23:59:59까지는 "오늘"로 간주

      const upcomingCampaigns: any[] = []

      campaignSnapshot.forEach((doc) => {
        const campaignData = {
          firestoreId: doc.id, // 실제 Firestore 문서 ID
          ...doc.data(), // 문서 내부 데이터
          id: doc.id, // Firestore 문서 ID로 덮어쓰기
        } as any

        const step2 = campaignData.step2
        if (!step2 || !step2.period) {
          return
        }

        // 날짜 파싱 (Firestore Timestamp 또는 문자열 형태)
        let startDate: Date

        try {
          // Firestore Timestamp 객체인 경우
          if (step2.period.toDate) {
            startDate = step2.period.toDate()
          }
          else {
            startDate = new Date(step2.period)
          }

          // 날짜 유효성 검사
          if (Number.isNaN(startDate.getTime())) {
            return
          }

          // 시간을 00:00:00으로 설정하여 날짜만 비교
          startDate.setHours(0, 0, 0, 0)

          // 시작일이 현재 날짜보다 미래인 캠페인만 선택 (내일부터 시작하는 캠페인들)
          const isUpcoming = startDate > currentDate

          if (isUpcoming) {
            upcomingCampaigns.push(campaignData)
          }
        }
        catch (dateError) {
          console.warn(`캠페인 ${campaignData.id}: 날짜 파싱 실패`, dateError)
        }
      })

      // 시작일 기준으로 정렬 (가장 빨리 시작할 캠페인부터)
      upcomingCampaigns.sort((a, b) => {
        const aStartDate = a.step2?.period?.toDate ? a.step2.period.toDate() : new Date(a.step2?.period)
        const bStartDate = b.step2?.period?.toDate ? b.step2.period.toDate() : new Date(b.step2?.period)
        return aStartDate.getTime() - bStartDate.getTime()
      })

      documents.value = upcomingCampaigns
    }
    catch (error) {
      console.error('진행 예정 캠페인 가져오기 중 오류:', error)
      documents.value = []
    }
    finally {
      loading.value = false
    }
  }

  // 특정 캠페인의 AI 분석 결과(키워드) 가져오기
  async function _getCampaignKeywords(campaignId: string) {
    try {
      const docRef = doc(db, 'campaign', campaignId)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const campaignData = docSnap.data()
        return campaignData.keywords || null
      }
      else {
        return null
      }
    }
    catch (error) {
      console.error('특정 캠페인 AI 분석 결과 가져오기 중 오류:', error)
      return null
    }
  }

  // 키워드가 있는 캠페인들 가져오기 (AI 분석이 완료된 캠페인들)
  async function _getCampaignsWithKeywords() {
    loading.value = true
    documents.value = []
    try {
      const campaignSnapshot = await getDocs(collection(db, 'campaign'))

      const campaignsWithKeywords: any[] = []
      campaignSnapshot.forEach((doc) => {
        const campaignData = { id: doc.id, ...doc.data() } as any
        // keywords 필드가 존재하고 비어있지 않은 캠페인만 필터링
        if (campaignData.keywords && campaignData.keywords !== null) {
          campaignsWithKeywords.push(campaignData)
        }
      })

      documents.value = campaignsWithKeywords
      return campaignsWithKeywords
    }
    catch (error) {
      console.error('키워드가 있는 캠페인들 가져오기 중 오류:', error)
      documents.value = []
      return []
    }
    finally {
      loading.value = false
    }
  }

  // 테스트용 - 기존 캠페인에 키워드 추가
  async function _testAddKeywordToExistingCampaign(campaignId: string) {
    return await _addKeywordToExistingCampaign(campaignId)
  }

  // AI 분석 결과를 기존 캠페인의 keywords 배열에 저장하는 함수 (CampaignAdd.vue에서 사용)
  async function saveAnalysisResult(campaignId: string, analysisResult: CampaignAnalysisResult) {
    try {
      if (!campaignId) {
        throw new Error('캠페인 ID가 필요합니다.')
      }

      if (!analysisResult) {
        throw new Error('분석 결과가 필요합니다.')
      }

      // campaign 컬렉션의 해당 문서에서 기존 keywords 배열 가져오기
      const docRef = doc(db, 'campaign', campaignId)
      const docSnap = await getDoc(docRef)

      let existingKeywords: string[] = []
      if (docSnap.exists()) {
        const data = docSnap.data()
        existingKeywords = data.keywords || []
      }

      // 새로운 키워드들을 기존 배열에 추가 (중복 제거)
      const newKeywords = analysisResult.keywords || []
      const combinedKeywords = [...new Set([...existingKeywords, ...newKeywords])]

      // Firestore에서 해당 캠페인 문서의 keywords 필드 업데이트
      await updateDoc(docRef, {
        keywords: combinedKeywords,
        analyzedAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      })
      systemStore.showSuccess('AI 분석 결과가 저장되었습니다.')

      // CampaignAdd.vue에서 기대하는 문서 ID 반환
      return campaignId
    }
    catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      systemStore.showError(`AI 분석 결과 저장 실패: ${errorMessage}`)
      return null
    }
  }

  // 백그라운드에서 캠페인 AI 분석을 수행하는 함수 (사용자 대기 없음)
  async function saveAnalysisResultBackground(campaignId: string, campaignData: any) {
    try {
      // 백그라운드 처리 - 사용자는 기다리지 않음
      Promise.resolve().then(async () => {
        try {
          // AI 분석 수행
          const analysisResult = await analyzeCampaignData(campaignData)

          if (analysisResult) {
            // campaign 컬렉션의 해당 문서에서 기존 keywords 배열 가져오기
            const docRef = doc(db, 'campaign', campaignId)
            const docSnap = await getDoc(docRef)

            let existingKeywords: string[] = []
            if (docSnap.exists()) {
              const data = docSnap.data()
              existingKeywords = data.keywords || []
            }

            // 새로운 키워드들을 기존 배열에 추가 (중복 제거)
            const newKeywords = analysisResult.keywords || []
            const combinedKeywords = [...new Set([...existingKeywords, ...newKeywords])]

            // Firestore에서 해당 캠페인 문서의 keywords 필드 업데이트
            await updateDoc(docRef, {
              keywords: combinedKeywords,
              analyzedAt: new Date().toISOString(),
              updatedAt: serverTimestamp(),
            })
          }
        }
        catch (error) {
          console.error('백그라운드 캠페인 AI 분석 실패:', error)
        }
      })

      // 즉시 성공 반환 (사용자는 기다리지 않음)
      return true
    }
    catch (error) {
      console.error('백그라운드 캠페인 AI 분석 실패:', error)
      return false
    }
  }

  // 크리에이터와 캠페인 키워드 유사도 계산
  function calculateKeywordSimilarity(creatorKeywords: string[], campaignKeywords: string[]): number {
    if (!creatorKeywords || !campaignKeywords || creatorKeywords.length === 0 || campaignKeywords.length === 0) {
      return 0
    }

    // 키워드 정규화 (소문자 변환, 공백 제거)
    const normalizedCreatorKeywords = creatorKeywords.map(k => k.toLowerCase().trim())
    const normalizedCampaignKeywords = campaignKeywords.map(k => k.toLowerCase().trim())

    // 교집합 계산
    const intersection = normalizedCreatorKeywords.filter(keyword =>
      normalizedCampaignKeywords.includes(keyword),
    )

    // 합집합 계산
    const union = [...new Set([...normalizedCreatorKeywords, ...normalizedCampaignKeywords])]

    // Jaccard 유사도 계산 (교집합 / 합집합)
    const jaccardSimilarity = intersection.length / union.length

    // 0-100 점수로 변환
    return Math.round(jaccardSimilarity * 100)
  }

  // 크리에이터에게 맞는 추천 캠페인 가져오기
  async function getRecommendedCampaignsForCreator(creatorId: string, limit: number = 4): Promise<any[]> {
    try {
      // 1. 모든 완료된 캠페인 가져오기
      await getCompleteCampaigns()
      const allCampaigns = documents.value

      if (allCampaigns.length === 0) {
        return []
      }

      // 2. 크리에이터 키워드 가져오기
      const creatorStore = useCreatorStore()
      await creatorStore.getCreators()
      const creator = creatorStore.creators.find((c: any) => c.id === creatorId)

      if (!creator || !creator.keywords || creator.keywords.length === 0) {
        console.warn('크리에이터 키워드가 없어 기본 추천을 사용합니다.')
        return allCampaigns.slice(0, limit)
      }

      const creatorKeywords = creator.keywords

      // 3. 각 캠페인과 유사도 계산
      const campaignScores = allCampaigns.map((campaign: any) => {
        const campaignKeywords = campaign.keywords || []
        const similarity = calculateKeywordSimilarity([...creatorKeywords], [...campaignKeywords])

        return {
          campaign,
          similarity,
          id: campaign.id,
        }
      })

      // 4. 유사도 순으로 정렬 (내림차순)
      campaignScores.sort((a, b) => b.similarity - a.similarity)

      // 5. 상위 캠페인 반환
      const recommendedCampaigns = campaignScores
        .slice(0, limit)
        .map(item => item.campaign)

      return recommendedCampaigns
    }
    catch (error) {
      console.error('추천 캠페인 가져오기 중 오류:', error)
      return []
    }
  }

  // 캠페인에 어울리는 크리에이터 추천 (getRecommendedCampaignsForCreator의 반대 방향).
  // docs/vibe-matching-design.md 4장 결정 (a) 2차 정렬을 따른다: 키워드 유사도로
  // 1차 정렬한 뒤, 동점 그룹 안에서만 creatorRecommendationScores의 finalScore로
  // 2차 정렬한다(app/utils/creatorMatching.ts). 이미 지원한 크리에이터는 제외한다 —
  // 이 목록은 "광고주가 먼저 초대할 새 후보"를 보여주는 용도라 지원자 목록과 겹칠
  // 필요가 없다.
  // 호출부(CampaignDetail.vue)가 "추천 결과 없음"과 "조회 실패"를 구분해 렌더링할 수
  // 있도록, 여기서는 에러를 삼키지 않고 그대로 던진다 — 호출부가 try/catch로 처리한다.
  async function getRecommendedCreatorsForCampaign(campaignData: any, limit: number = 20): Promise<any[]> {
    const campaignKeywords = campaignData?.keywords || []

    // 1. 전체 크리에이터 중 아직 지원하지 않은 크리에이터만 후보로 남긴다
    const creatorStore = useCreatorStore()
    await creatorStore.getCreators()
    const alreadyAppliedIds = new Set(Object.keys(campaignData?.creator || {}))
    const candidates = creatorStore.creators.filter((creator: any) => !alreadyAppliedIds.has(creator.id))

    if (candidates.length === 0) {
      return []
    }

    // 2. 키워드 유사도 계산 (1차 정렬 기준)
    const candidatesWithSimilarity = candidates.map((creator: any) => ({
      ...creator,
      keywordSimilarity: calculateKeywordSimilarity(creator.keywords || [], campaignKeywords),
      matchedKeywords: findMatchedKeywords(creator.keywords || [], campaignKeywords),
    }))

    // 3. creatorRecommendationScores 프로젝션 조회 (2차 정렬 기준)
    const scoresSnapshot = await getDocs(collection(db, 'creatorRecommendationScores'))
    const scoresByCreatorId: Record<string, { finalScore: number }> = {}
    scoresSnapshot.forEach((scoreDoc) => {
      const data = scoreDoc.data()
      if (data.creatorId) {
        scoresByCreatorId[data.creatorId] = { finalScore: data.finalScore }
      }
    })

    // 4. 1차 키워드 유사도 → 2차 vibe 점수 순으로 정렬 후 상위 N개 반환
    const ranked = rankCreatorsByKeywordThenVibeScore(candidatesWithSimilarity, scoresByCreatorId)

    return ranked.slice(0, limit).map(item => ({
      ...item.creator,
      keywordSimilarity: item.keywordSimilarity,
      finalScore: item.finalScore,
      recommendationReason: buildRecommendationReason({
        matchedKeywords: (item.creator as any).matchedKeywords || [],
        finalScore: item.finalScore,
        grade: (item.creator as any).grade,
      }),
    }))
  }

  async function handleModalAction(campaign: any) {
    // campaigns 컬렉션에서 complete 필드를 false로 변경

    const campaignDocRef = doc(db, 'campaign', campaign.campaignId)
    await updateDoc(campaignDocRef, {
      complete: false,
      updatedAt: new Date(),
    })

    // 로컬 상태에서 해당 캠페인의 complete 상태를 업데이트 (재로딩 없이)
    const campaignIndex = documents.value.findIndex((c: any) => c.campaignId === campaign.campaignId)
    if (campaignIndex !== -1) {
      // 캠페인이 더 이상 모달을 표시하지 않도록 로컬에서 처리
      documents.value[campaignIndex].complete = false
    }
  }

  async function loadCampaignData(props: any) {
    try {
      loading.value = true

      if (!userStore.userStoreData?.uid) {
        return
      }

      // 1) 문서 ID로 우선 조회 (올바른 컬렉션명 'campaign' 사용)
      const campaignDocRef = doc(db, 'campaign', props.campaignId)
      const campaignDoc = await getDoc(campaignDocRef)

      if (campaignDoc.exists()) {
        const campaign: any = campaignDoc.data()

        if (campaign.advertiserId === userStore.userStoreData.uid) {
          document.value = campaign // campaignData
          document.value.id = campaignDoc.id // campaignDocId
        }
        else {
          console.error('[CampaignDetail] 본인 캠페인이 아님. campaign.advertiserId:', campaign.advertiserId, 'userStore.uid:', userStore.userStoreData.uid)
        }
      }
      else {
        // 2) 내부 id 필드로 조회 (올바른 컬렉션명 'campaign' 사용)
        const q = query(collection(db, 'campaign'), where('id', '==', props.campaignId))
        const snap = await getDocs(q)

        if (!snap.empty) {
          const first = snap.docs[0]
          const campaign: any = first?.data()

          if (campaign.advertiserId === userStore.userStoreData.uid) {
            document.value = campaign as any
            document.value.id = first?.id as string
          }
          else {
            console.error('[CampaignDetail] 본인 캠페인이 아님 (내부 id). campaign.advertiserId:', campaign.advertiserId, 'userStore.uid:', userStore.userStoreData.uid)
          }
        }
        /* else {
          console.error('[CampaignDetail] 내부 id로도 캠페인 문서를 찾을 수 없음:', props.campaignId)

          // 3) 디버깅을 위해 해당 사용자의 모든 캠페인 조회
          const userCampaignsQuery = query(collection(db, 'campaign'), where('advertiserId', '==', userStore.userStoreData.uid))
          const userCampaignsSnap = await getDocs(userCampaignsQuery)

          userCampaignsSnap.docs.forEach((doc, index) => {
            const data = doc.data()
          })
        } */
      }
    }
    catch (error) {
      console.error('캠페인 데이터 로드 중 오류:', error)
    }
    finally {
      loading.value = false
    }
  }

  // 캠페인 데이터를 Firestore에 저장
  async function saveCampaignData(props: any) {
    try {
      if (!userStore.userStoreData?.uid) {
        return
      }

      // 브랜드명은 문서 필드로 저장하되, 캠페인 ID는 랜덤으로 생성
      const brandName = document.value.step1.brandName.trim()
      if (!brandName) {
        return
      }

      // 편집 모드 여부 판단: 문서 ID가 있으면 업데이트, 없으면 신규 생성
      if (props.documentId) {
        // 상태 정규화 유틸
        const normalizeStatus = (status?: string | null): 'pending' | 'approved' | 'rejected' => {
          const s = (status || '').toLowerCase()
          if (s === 'rejected' || s === 'reject')
            return 'rejected'
          if (s === 'approved' || s === 'approve' || s === 'complete' || s === 'completed')
            return 'approved'
          return 'pending'
        }

        const prevStatus = props.initialData?.status
        const normalizedPrev = normalizeStatus(prevStatus as string | undefined)
        const nextStatus: 'pending' | 'approved' | 'rejected'
          = normalizedPrev === 'rejected' ? 'pending' : (normalizedPrev as 'pending' | 'approved' | 'rejected')

        // 업데이트 페이로드: createdAt은 보존, updatedAt 갱신, 상태는 재승인 대기로 초기화
        const updatePayload = {
          advertiserId: userStore.userStoreData.uid,
          step1: document.value.step1,
          step2: document.value.step2,
          step3: document.value.step3,
          step4: document.value.step4,
          brandName,
          updatedAt: new Date(),
          status: nextStatus,
          complete: false,
        }
        await updateDoc(doc(db, 'campaign', props.documentId), updatePayload)

        // 편집 모드에서는 재생성/AI 저장을 수행하지 않고 상위로 완료 이벤트 통지
        // emit('submit')
        return 'submit'
      }
      else {
        // 고유한 캠페인 ID 생성 (내부 식별용)
        const campaignId = `campaign_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

        const campaignPayload = {
          id: campaignId,
          advertiserId: userStore.userStoreData.uid, // advertiser ID 추가
          step1: document.value.step1,
          step2: document.value.step2,
          step3: document.value.step3,
          step4: document.value.step4,
          brandName,
          createdAt: new Date(),
          updatedAt: new Date(),
          status: 'pending' as const, // 승인 대기 상태
          complete: false,
        }

        // 1) campaigns 컬렉션에 먼저 저장
        const createdDocRef = await addDoc(collection(db, 'campaign'), campaignPayload)

        // 2) AI 분석 실행
        try {
          const analysisResult = await analyzeCampaignData(campaignPayload)

          if (analysisResult) {
            // 3) AI 분석 결과를 campaignAI 컬렉션에 저장 (캠페인 Firestore 문서 ID 기준으로 연결)
            const savedDocId = await saveAnalysisResult(createdDocRef.id, analysisResult)

            if (savedDocId) {
              console.warn('AI 분석 결과가 성공적으로 저장되었습니다. 문서 ID:', savedDocId)
            }
            else {
              console.error('AI 분석 결과 저장에 실패했습니다.')
            }
          }
          else {
            console.error('AI 분석 결과가 null입니다.')
          }
        }
        catch (analysisError) {
          console.error('AI 분석 중 오류 발생:', analysisError)
        }

        // 성공 후 다음 단계로 진행
        // nextStep()
        return 'next'
      }
    }
    catch (error) {
      console.error('캠페인 저장 중 오류 발생:', error)
    }
  }

  // 브랜드 이미지 파일 핸들러
  async function handleBrandImageFile(input: any) {
    const file = input.files?.[0]
    if (file) {
      // 파일 크기 확인 (5MB 제한)
      const maxSize = 5 * 1024 * 1024 // 5MB
      if (file.size > maxSize) {
        selectedBrandImageName.value = '파일 크기는 5MB 이하여야 합니다.'
        return
      }

      // 파일 타입 확인
      if (!file.type.startsWith('image/')) {
        selectedBrandImageName.value = '이미지 파일만 업로드 가능합니다.'
        return
      }

      try {
        isUploadingImage.value = true
        selectedBrandImageName.value = '업로드 중...'

        if (!userStore.userStoreData?.uid) {
          throw new Error('로그인이 필요합니다.')
        }

        if (!auth.currentUser) {
          throw new Error('Firebase 인증이 필요합니다.')
        }

        // 토큰 새로고침 시도
        try {
          await auth.currentUser.getIdToken(true) // force refresh
        }
        catch (tokenError) {
          console.warn('토큰 새로고침 실패:', tokenError)
        }

        // 파일명을 더 안전하게 정리
        const fileExtension = file.name.split('.').pop() || 'png'
        const cleanFileName = `brand_${Date.now()}.${fileExtension}`
        const fileName = `campaign/advertiser-brand_image/${userStore.userStoreData.uid}/${cleanFileName}`

        const imageRef = storageRef(storage, fileName)

        // 메타데이터 추가
        const metadata = {
          contentType: file.type,
          customMetadata: {
            originalName: file.name,
            uploadedBy: userStore.userStoreData.uid,
            uploadedAt: new Date().toISOString(),
          },
        }

        const snapshot = await uploadBytes(imageRef, file, metadata)

        const downloadURL = await getDownloadURL(snapshot.ref)

        // 캠페인 데이터에 이미지 URL 저장
        document.value.step1.brandImage = downloadURL
        selectedBrandImageName.value = file.name
      }
      catch (error: any) {
        let errorMessage = '업로드 실패 - 다시 시도해주세요'

        if (error.code === 'storage/unauthorized') {
          errorMessage = '업로드 권한이 없습니다. 로그인을 확인해주세요.'
        }
        else if (error.code === 'storage/canceled') {
          errorMessage = '업로드가 취소되었습니다.'
        }
        else if (error.code === 'storage/unknown') {
          errorMessage = '알 수 없는 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
        }
        else if (error.code === 'storage/quota-exceeded') {
          errorMessage = '저장 용량이 초과되었습니다.'
        }

        selectedBrandImageName.value = errorMessage
      }
      finally {
        isUploadingImage.value = false
      }
    }
  }

  // 캠페인 이미지 파일 핸들러
  async function handleCampaignImageFile(input: any) {
    const file = input.files?.[0]
    if (file) {
      // 파일 크기 확인 (5MB 제한)
      const maxSize = 5 * 1024 * 1024 // 5MB
      if (file.size > maxSize) {
        selectedCampaignImageName.value = '파일 크기는 5MB 이하여야 합니다.'
        return
      }

      // 파일 타입 확인
      if (!file.type.startsWith('image/')) {
        selectedCampaignImageName.value = '이미지 파일만 업로드 가능합니다.'
        return
      }

      try {
        isUploadingCampaignImage.value = true
        selectedCampaignImageName.value = '업로드 중...'

        const userStore = useUserStore()

        if (!userStore.userStoreData?.uid) {
          throw new Error('로그인이 필요합니다.')
        }

        if (!auth.currentUser) {
          throw new Error('Firebase 인증이 필요합니다.')
        }

        // 토큰 새로고침 시도
        try {
          await auth.currentUser.getIdToken(true) // force refresh
          console.warn('Firebase Auth 토큰 새로고침 완료')
        }
        catch (tokenError) {
          console.warn('토큰 새로고침 실패:', tokenError)
        }

        // 파일명을 더 안전하게 정리
        const fileExtension = file.name.split('.').pop() || 'png'
        const cleanFileName = `campaign_${Date.now()}.${fileExtension}`
        const fileName = `campaign/advertiser-campaign_image/${userStore.userStoreData.uid}/${cleanFileName}`

        const imageRef = storageRef(storage, fileName)

        // 메타데이터 추가
        const metadata = {
          contentType: file.type,
          customMetadata: {
            originalName: file.name,
            uploadedBy: userStore.userStoreData.uid,
            uploadedAt: new Date().toISOString(),
          },
        }

        const snapshot = await uploadBytes(imageRef, file, metadata)

        const downloadURL = await getDownloadURL(snapshot.ref)

        // 캠페인 데이터에 이미지 URL 저장
        document.value.step2.campaignImage = downloadURL
        selectedCampaignImageName.value = file.name
      }
      catch (error: any) {
        let errorMessage = '업로드 실패 - 다시 시도해주세요'

        if (error.code === 'storage/unauthorized') {
          errorMessage = '업로드 권한이 없습니다. 로그인을 확인해주세요.'
        }
        else if (error.code === 'storage/canceled') {
          errorMessage = '업로드가 취소되었습니다.'
        }
        else if (error.code === 'storage/unknown') {
          errorMessage = '알 수 없는 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
        }
        else if (error.code === 'storage/quota-exceeded') {
          errorMessage = '저장 용량이 초과되었습니다.'
        }

        selectedCampaignImageName.value = errorMessage
      }
      finally {
        isUploadingCampaignImage.value = false
      }
    }
  }

  // 캠페인에 참여한 크리에이터들의 정보 가져오기
  async function getCampaignCreators(campaignData: any) {
    try {
      const creators = []

      if (!campaignData?.creator) {
        return []
      }

      // campaign.creator 객체에서 크리에이터 ID들 추출
      const creatorIds = Object.keys(campaignData.creator)

      if (creatorIds.length === 0) {
        return []
      }

      // 각 크리에이터 ID로 creator 컬렉션에서 정보 조회
      for (const creatorId of creatorIds) {
        try {
          const creatorDocRef = doc(db, 'creator', creatorId)
          const creatorDoc = await getDoc(creatorDocRef)

          if (creatorDoc.exists()) {
            const creatorData = creatorDoc.data()

            // creator.ts의 구조에 맞게 프로필 이미지 찾기
            let profileImageUrl = ''
            if (creatorData.sign?.profileImageUrl) {
              profileImageUrl = creatorData.sign.profileImageUrl
            }
            else if (creatorData.profileImageUrl) {
              profileImageUrl = creatorData.profileImageUrl
            }
            else if (creatorData.settlementInfo?.profileImageUrl) {
              profileImageUrl = creatorData.settlementInfo.profileImageUrl
            }

            const creatorInfo = {
              id: creatorId,
              name: creatorData.name || creatorData.nickname || 'Unknown',
              avatar: profileImageUrl || null,
              // campaign 데이터에서 해당 크리에이터의 추가 정보 (링크 등)
              ...campaignData.creator[creatorId],
            }
            creators.push(creatorInfo)
          }
          else {
            console.warn(`크리에이터 ${creatorId} 문서가 존재하지 않음`)
          }
        }
        catch {
          // 실패한 경우에도 기본 정보로 추가
          creators.push({
            id: creatorId,
            name: 'Unknown Creator',
            avatar: null,
            ...campaignData.creator[creatorId],
          })
        }
      }

      return creators
    }
    catch {
      return []
    }
  }

  // 캠페인 complete 상태를 false로 변경
  async function updateCampaignCompleteStatus(props: any) {
    if (!props.campaignId) {
      return
    }

    try {
      const docRef = doc(db, 'campaign', props.campaignId!)
      await updateDoc(docRef, {
        complete: false,
        updatedAt: serverTimestamp(),
      })
    }
    catch (error) {
      console.error('캠페인 complete 상태 업데이트 중 오류:', error)
    }
  }

  return {
    loading,
    documents,
    document,
    uploadDocument,
    currentView,
    selectedBrandImageName,
    isUploadingImage,
    isEditMode,
    selectedCampaignImageName,
    isUploadingCampaignImage,
    getCampaignList,
    getCampaign,
    addCampaign,
    updateCampaign,
    deleteCampaign,
    analyzeCampaignData,
    saveAnalysisResult,
    saveAnalysisResultBackground,
    getCompleteCampaigns,
    getAllCampaigns,
    getCampaignsByAdvertiser,
    getActiveCampaigns,
    getUpcomingCampaigns,
    getCampaignKeywords: _getCampaignKeywords,
    getCampaignsWithKeywords: _getCampaignsWithKeywords,
    addKeywordToExistingCampaign: _addKeywordToExistingCampaign,
    testAddKeywordToExistingCampaign: _testAddKeywordToExistingCampaign,
    calculateKeywordSimilarity,
    getRecommendedCampaignsForCreator,
    getRecommendedCreatorsForCampaign,
    handleModalAction,
    loadCampaignData,
    saveCampaignData,
    handleBrandImageFile,
    handleCampaignImageFile,
    updateCampaignCompleteStatus,
    getCampaignCreators,
  }
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useCampaignStore, import.meta.hot))
